import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { useLanguage, LANGUAGES } from '@/contexts/LanguageContext';
import { useProfile } from '@/hooks/useProfile';
import type { Company } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  LogOut, Upload, Trash2, Ban, Loader2, ShieldCheck, Mail,
  Building2, Crown, Sparkles, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { getSortedIanaTimeZones } from '@/lib/timezones';
import { ImageUploadError, uploadImage } from '@/lib/imageUpload';
import { SubscriptionTab } from '@/components/settings/SubscriptionTab';
import { NotificationPreferencesTab } from '@/components/settings/NotificationPreferencesTab';

/**
 * ProfilePage — Pricing v3.2 (21/05/26).
 *
 * Refactored from a single scrolling page into a tabbed layout:
 *   - "Account":   identity, preferences, sign-in methods, reset password,
 *                  logout, danger zone
 *   - "Piano":     subscription tab
 *   - "Aziende":   memberships list
 *
 * Hero header on top: avatar large + gradient bg + name + email + account
 * type badge + owned-companies count.
 */

const VALID_PROFILE_TABS = ['account', 'billing', 'notifications', 'companies'] as const;
type ProfileTab = typeof VALID_PROFILE_TABS[number];

function ProfileHeader() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const ownedCount = Object.values(user?.memberships ?? {}).filter(
    (m) => m.permission === 'admin',
  ).length;
  const accountType = user?.accountType ?? 'individual';
  const isInvitedMember = accountType === 'invited_member';
  // v3.2 polish (21/05/26): the `accountType` field can lag the actual state
  // (e.g. a Stripe cancellation reverts plan→trial but the 5 client companies
  // remain). Derive "Agency Owner" from owned-companies count so the badges
  // never contradict each other ("Account individuale" + "5 aziende possedute").
  // v3.4 (25/05/26): an `invited_member` is NEVER an agency owner — explicit
  // override to avoid the heuristic mistakenly promoting them.
  const isAgencyOwnerEffective =
    !isInvitedMember && (accountType === 'agency_owner' || ownedCount >= 2);

  const accountTypeLabel = isAgencyOwnerEffective
    ? t('profile.header.agencyOwner')
    : isInvitedMember
    ? t('profile.header.invitedMember')
    : accountType === 'agency_member'
    ? t('profile.header.agencyMember')
    : t('profile.header.individual');

  // v3.4: show owner reference when user is an invited member
  const ownerLabel =
    user?.effectiveOwner?.displayName ?? user?.effectiveOwner?.email ?? null;

  return (
    <div className="relative overflow-hidden rounded-lg border border-border bg-gradient-to-br from-brand-primary/10 via-brand-accent/5 to-transparent">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-brand-accent/20 via-transparent to-transparent pointer-events-none" />
      <div className="relative px-6 py-6 sm:px-8 sm:py-8">
        <div className="flex items-start gap-4 sm:gap-6">
          {/* Avatar */}
          <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-background border-4 border-background shadow-md overflow-hidden flex items-center justify-center text-2xl sm:text-3xl font-semibold text-brand-primary shrink-0">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={`${user.displayName || user.email} avatar`}
                className="h-full w-full object-cover"
              />
            ) : (
              <span>{user?.displayName?.[0]?.toUpperCase() || 'U'}</span>
            )}
          </div>
          {/* Identity + badges */}
          <div className="flex-1 min-w-0 space-y-2">
            <div>
              <h1 className="text-xl sm:text-2xl font-display font-semibold truncate">
                {user?.displayName || t('profile.title')}
              </h1>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={
                  isAgencyOwnerEffective
                    ? 'gap-1 border-brand-accent text-brand-accent bg-background'
                    : isInvitedMember
                    ? 'gap-1 border-muted-foreground/40 text-muted-foreground bg-background'
                    : 'gap-1 bg-background'
                }
              >
                {isAgencyOwnerEffective && <Crown className="h-3 w-3" />}
                {!isAgencyOwnerEffective && isInvitedMember && <Users className="h-3 w-3" />}
                {!isAgencyOwnerEffective && !isInvitedMember && accountType === 'agency_member' && (
                  <Sparkles className="h-3 w-3" />
                )}
                {accountTypeLabel}
              </Badge>
              {isInvitedMember && ownerLabel && (
                <Badge variant="outline" className="gap-1 bg-background">
                  <Building2 className="h-3 w-3" />
                  {t('profile.header.memberOf', { owner: ownerLabel })}
                </Badge>
              )}
              {!isInvitedMember && ownedCount > 0 && (
                <Badge variant="outline" className="gap-1 bg-background">
                  <Building2 className="h-3 w-3" />
                  {t('profile.header.companiesOwned', { n: ownedCount })}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyAvatar({ name, logoUrl }: { name: string; logoUrl?: string }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const initial = name[0]?.toUpperCase() ?? 'C';

  if (logoUrl && !logoFailed) {
    return (
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className="h-9 w-9 rounded-md object-contain border border-border bg-background shrink-0"
        onError={() => setLogoFailed(true)}
      />
    );
  }

  return (
    <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
      {initial}
    </div>
  );
}

const profileActionRowClass =
  'flex items-center justify-between gap-3 rounded-lg border border-border/50 px-4 py-3';
const profileActionButtonClass =
  'inline-flex h-9 w-[8.75rem] shrink-0 items-center justify-center px-2 text-xs sm:text-sm';
const profileResetButtonClass =
  'inline-flex h-9 w-[10.5rem] shrink-0 items-center justify-center px-3 text-xs sm:text-sm';

const ProfilePage = () => {
  const { user, firebaseUser, authProviders, logout, refreshSession, setSelectedCompany } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const { displayName, setDisplayName, timeZone, setTimeZone, saving, handleSave } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  const [theme, setTheme] = useState('system');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);

  const [disableAccountOpen, setDisableAccountOpen] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState('');
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [companyById, setCompanyById] = useState<Record<string, Company>>({});

  // v3.4 (25/05/26) — Pending membership action for the memberships tab.
  // Discriminates between "leave" (non-owner removes themselves) and "delete"
  // (owner permanently deletes the company). The two actions hit different
  // endpoints and have different UX (confirm dialog copy + destructive typing).
  const [pendingMembershipAction, setPendingMembershipAction] = useState<
    { companyId: string; action: 'leave' | 'delete' } | null
  >(null);
  const [membershipActionBusy, setMembershipActionBusy] = useState(false);
  const [deleteCompanyTypedConfirm, setDeleteCompanyTypedConfirm] = useState('');

  // v3.4 (25/05/26) — Member confinement: invited_member users cannot manage
  // their plan. The Billing tab is hidden and deep-links to ?tab=billing are
  // rewritten to ?tab=account. Default `true` for back-compat with BE without
  // the canManagePlan field (treated as owner).
  const canManagePlan = user?.canManagePlan ?? true;

  // ── Tabs: sync with URL ?tab= ────────────────────────────────────────────
  const initialTab = (() => {
    const raw = searchParams.get('tab');
    if (!raw || !(VALID_PROFILE_TABS as readonly string[]).includes(raw)) return 'account';
    if (raw === 'billing' && !canManagePlan) return 'account';
    return raw as ProfileTab;
  })();
  const [activeTab, setActiveTab] = useState<ProfileTab>(initialTab);

  useEffect(() => {
    const raw = searchParams.get('tab');
    if (raw === 'security') {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'account');
      setSearchParams(next, { replace: true });
      setActiveTab('account');
      return;
    }
    // v3.4: redirect billing deep-link for members
    if (raw === 'billing' && !canManagePlan) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'account');
      setSearchParams(next, { replace: true });
      setActiveTab('account');
      return;
    }
    if (raw && (VALID_PROFILE_TABS as readonly string[]).includes(raw) && raw !== activeTab) {
      setActiveTab(raw as ProfileTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canManagePlan]);

  const onTabChange = (v: string) => {
    if (!(VALID_PROFILE_TABS as readonly string[]).includes(v)) return;
    if (v === 'billing' && !canManagePlan) return;
    setActiveTab(v as ProfileTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  const memberships = Object.values(user?.memberships ?? {});

  useEffect(() => {
    if (!memberships.length) return;

    let cancelled = false;

    (async () => {
      const next: Record<string, Company> = {};

      try {
        const listed = await api.companies.list();
        for (const company of listed) {
          next[company.id] = company;
        }
      } catch {
        // non-blocking
      }

      await Promise.all(
        memberships.map(async (membership) => {
          const existing = next[membership.companyId] ?? membership.company;
          if ((existing.name || '').trim() && existing.logoUrl) {
            next[membership.companyId] = existing;
            return;
          }
          try {
            const full = await api.companies.get(membership.companyId);
            if (full) {
              next[membership.companyId] = { ...existing, ...full };
            } else {
              next[membership.companyId] = existing;
            }
          } catch {
            next[membership.companyId] = existing;
          }
        }),
      );

      if (!cancelled) {
        setCompanyById(next);
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(user?.memberships)]);

  const handleSelectCompany = (companyId: string) => {
    const membership = user?.memberships[companyId];
    const company = companyById[companyId] ?? membership?.company;
    if (!company?.id) return;
    void setSelectedCompany(company);
    navigate('/dashboard');
  };

  // v3.4 (25/05/26) — Resolve company name for use in dialogs/toasts.
  const getMembershipCompanyName = (cid: string): string =>
    (companyById[cid]?.name)
    ?? (user?.memberships?.[cid]?.company?.name)
    ?? '';

  // v3.4 (25/05/26) — Confirm + execute the pending membership action
  // (either "leave" for non-owners or "delete" for owners). The action is
  // discriminated server-side by the endpoint we call.
  const handleConfirmMembershipAction = async () => {
    if (!pendingMembershipAction) return;
    const { companyId, action } = pendingMembershipAction;
    const companyName = getMembershipCompanyName(companyId);
    setMembershipActionBusy(true);

    try {
      if (action === 'leave') {
        await api.auth.leaveCompany(companyId);
        toast.success(t('memberships.leave.success', { company: companyName }));
      } else {
        // Owner permanently deletes the company. The BE clears all sub-data
        // (job positions, candidates, members, billing) — same endpoint used
        // by Settings → Generale → Elimina azienda.
        await api.companies.delete(companyId);
        toast.success(t('memberships.delete.success', { company: companyName }));
      }
      setPendingMembershipAction(null);
      setDeleteCompanyTypedConfirm('');
      // Refresh the session so memberships + accountType update immediately
      try { await refreshSession(); } catch { /* non-fatal */ }
    } catch (err: any) {
      if (action === 'leave') {
        const status = err?.status;
        const msg: string = err?.message ?? '';
        let localized: string | undefined;
        if (status === 403) {
          if (/own/i.test(msg)) {
            localized = t('memberships.leave.error.owner');
          } else if (/last admin/i.test(msg)) {
            localized = t('memberships.leave.error.last_admin');
          }
        }
        toast.error(localized || t('memberships.leave.error.fallback'));
      } else {
        toast.error(err?.message || t('memberships.delete.error.fallback'));
      }
    } finally {
      setMembershipActionBusy(false);
    }
  };

  const handleLanguageChange = async (v: 'en' | 'it') => {
    setLanguage(v);
    try {
      await api.auth.updateProfile({ preferredLanguage: v });
      await refreshSession();
    } catch {
      toast.error(t('profile.languageSaveError'));
    }
  };

  const handleThemeChange = (val: string) => {
    setTheme(val);
    if (val === 'dark') document.documentElement.classList.add('dark');
    else if (val === 'light') document.documentElement.classList.remove('dark');
    else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    }
  };

  const handleAvatarFileSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !firebaseUser) return;

    setUploadingAvatar(true);
    try {
      const url = await uploadImage(file, {
        prefix: 'user-avatars',
        ownerId: firebaseUser.uid,
      });
      await api.auth.updateProfile({ avatarUrl: url });
      await refreshSession();
      toast.success(t('profile.uploadAvatar.success'));
    } catch (err) {
      if (err instanceof ImageUploadError) {
        if (err.code === 'too_large') toast.error(t('profile.uploadAvatar.tooLarge'));
        else if (err.code === 'invalid_type') toast.error(t('profile.uploadAvatar.invalidType'));
        else toast.error(`${t('profile.uploadAvatar.failed')} (${err.message})`);
      } else {
        toast.error(t('profile.uploadAvatar.failed'));
      }
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user?.email || sendingPasswordReset) return;
    setSendingPasswordReset(true);
    setPasswordResetSent(false);
    try {
      await api.auth.sendPasswordReset(user.email);
      setPasswordResetSent(true);
      toast.success(t('profile.passwordResetSent'));
    } catch {
      toast.error(t('profile.passwordResetError'));
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const signInMethodsDesc =
    authProviders.google && authProviders.password
      ? t('profile.signInBothDesc')
      : authProviders.google
        ? t('profile.signInGoogleOnlyDesc')
        : t('profile.signInPasswordOnlyDesc');

  return (
    <div className="space-y-6 max-w-4xl mx-auto w-full">
      {/* ── Hero header ─────────────────────────────────────────────────── */}
      <ProfileHeader />

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <Tabs value={activeTab} onValueChange={onTabChange}>
        <div className="pb-2">
          <TabsList>
            <TabsTrigger value="account">{t('profile.tab.account')}</TabsTrigger>
            {canManagePlan && (
              <TabsTrigger value="billing">{t('profile.tab.billing')}</TabsTrigger>
            )}
            <TabsTrigger value="notifications">{t('profile.tab.notifications')}</TabsTrigger>
            <TabsTrigger value="companies">{t('profile.tab.companies')}</TabsTrigger>
          </TabsList>
        </div>

        {/* ── Account ──────────────────────────────────────────────────── */}
        <TabsContent value="account" className="mt-4 space-y-4">
          {/* Identity card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('profile.identitySection')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-xl font-semibold text-primary overflow-hidden">
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={`${user.displayName || user.email} avatar`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span>{user?.displayName?.[0] || 'U'}</span>
                  )}
                </div>
                <input
                  id="avatar-upload-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={handleAvatarFileSelected}
                  disabled={uploadingAvatar}
                />
                <Button asChild variant="outline" size="sm" className="gap-1.5" disabled={uploadingAvatar}>
                  <label htmlFor="avatar-upload-input" className="cursor-pointer">
                    {uploadingAvatar ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {t('profile.changeAvatar')}
                  </label>
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('profile.displayName')}</Label>
                  <Input value={displayName} onChange={e => setDisplayName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.email')}</Label>
                  <Input value={user?.email || ''} disabled className="bg-muted/50" />
                </div>
              </div>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {t('profile.saveChanges')}
              </Button>
            </CardContent>
          </Card>

          {/* Preferences card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('profile.preferencesSection')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('profile.timeZone')}</Label>
                <p className="text-xs text-muted-foreground">{t('profile.timeZoneDesc')}</p>
                <Select value={timeZone} onValueChange={setTimeZone}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('profile.timeZone')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[min(320px,50vh)] overflow-y-auto">
                    {getSortedIanaTimeZones().map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border/50">
                <div className="space-y-2">
                  <Label>{t('profile.theme')}</Label>
                  <Select value={theme} onValueChange={handleThemeChange}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">{t('profile.light')}</SelectItem>
                      <SelectItem value="dark">{t('profile.dark')}</SelectItem>
                      <SelectItem value="system">{t('profile.system')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('profile.language')}</Label>
                  <Select value={language} onValueChange={v => void handleLanguageChange(v as 'en' | 'it')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(l => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('profile.signInMethods')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {authProviders.google && (
                  <Badge variant="outline" className="gap-1 bg-background">
                    <ShieldCheck className="h-3 w-3" />
                    {t('profile.signInGoogle')}
                  </Badge>
                )}
                {authProviders.password && (
                  <Badge variant="outline" className="gap-1 bg-background">
                    <Mail className="h-3 w-3" />
                    {t('profile.signInEmailPassword')}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{signInMethodsDesc}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('profile.resetPassword')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1 pr-2 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {authProviders.password ? t('profile.passwordResetDesc') : t('profile.setPasswordDesc')}
                  </p>
                  {passwordResetSent && (
                    <p className="text-xs text-emerald-700 dark:text-emerald-400">
                      {t('profile.passwordResetSent')}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className={profileResetButtonClass}
                  onClick={() => void handleSendPasswordReset()}
                  disabled={sendingPasswordReset || !user?.email}
                >
                  {sendingPasswordReset ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {sendingPasswordReset ? t('profile.passwordResetSending') : t('profile.resetPasswordButton')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Logout */}
          <div className={profileActionRowClass}>
            <div className="min-w-0 flex-1 pr-2">
              <p className="text-sm font-medium">{t('profile.logout')}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t('profile.logoutDesc')}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className={`${profileActionButtonClass} text-destructive hover:bg-destructive hover:text-destructive-foreground`}
              onClick={logout}
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> {t('profile.logout')}
            </Button>
          </div>

          {/* Danger zone */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
              {t('profile.dangerZone')}
            </h3>
            <div className={profileActionRowClass}>
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-sm font-medium">{t('profile.disableAccount')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('profile.disableAccountDesc')}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className={`${profileActionButtonClass} text-destructive hover:bg-destructive hover:text-destructive-foreground`}
                onClick={() => setDisableAccountOpen(true)}
              >
                <Ban className="h-3.5 w-3.5 mr-1.5" /> {t('profile.disableAccountShort')}
              </Button>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3">
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-sm font-medium text-destructive">{t('profile.permanentDeleteAccount')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('profile.permanentDeleteAccountDesc')}</p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                className={profileActionButtonClass}
                onClick={() => setDeleteAccountOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t('profile.deleteAccountShort')}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ── Billing ──────────────────────────────────────────────────── */}
        {/*
          v3.2 (21/05/26): Subscription moved here from
          /settings?tab=subscription. SubscriptionTab is reused as-is —
          it already reads `selectedCompany` for company-scoped queries
          (status/usage/portal), and the BE Fase 3.1 pivot makes those
          resolve owner-first under the hood. The user-level plan is
          consistent across all owned companies.
        */}
        {canManagePlan && (
          <TabsContent value="billing" className="mt-4 space-y-4">
            <SubscriptionTab />
          </TabsContent>
        )}

        {/* ── Notifications (v3-notif, 27/05/26) ─────────────────────── */}
        <TabsContent value="notifications" className="mt-4 space-y-4">
          <NotificationPreferencesTab />
        </TabsContent>

        {/* ── Companies / Memberships ─────────────────────────────────── */}
        <TabsContent value="companies" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('profile.memberships')}</CardTitle>
            </CardHeader>
            <CardContent>
              {memberships.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">{t('profile.memberships.empty')}</p>
              ) : (
                <div className="space-y-1">
                  {memberships.map(m => {
                    const company = companyById[m.companyId] ?? m.company;
                    const displayName = (company.name || '').trim() || '—';
                    const logoUrl = company.logoUrl;
                    // v3.4: owner sees "Elimina" (delete the whole company),
                    // member sees "Lascia" (just removes themselves).
                    const isCompanyOwner = company.ownerUid === user?.id;
                    const actionLabel = isCompanyOwner
                      ? t('memberships.delete.cta')
                      : t('memberships.leave.cta');
                    const ActionIcon = isCompanyOwner ? Trash2 : LogOut;

                    return (
                      <div
                        key={m.companyId}
                        className="w-full flex items-center justify-between gap-3 py-3 px-2 -mx-2 rounded-md border-b border-border/30 last:border-0 hover:bg-muted/50 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => handleSelectCompany(m.companyId)}
                          className="flex items-center gap-3 min-w-0 flex-1 text-left"
                        >
                          <CompanyAvatar name={displayName} logoUrl={logoUrl} />
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium truncate">{displayName}</span>
                            {m.jobRole && (
                              <span className="text-xs text-muted-foreground truncate">{m.jobRole}</span>
                            )}
                          </div>
                        </button>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="capitalize bg-primary/5 border-primary/20 text-primary">
                            {m.permission}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteCompanyTypedConfirm('');
                              setPendingMembershipAction({
                                companyId: m.companyId,
                                action: isCompanyOwner ? 'delete' : 'leave',
                              });
                            }}
                          >
                            <ActionIcon className="h-3.5 w-3.5 mr-1" />
                            {actionLabel}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* v3.4 Membership action confirmation — leave (member) or delete (owner) */}
      <Dialog
        open={!!pendingMembershipAction}
        onOpenChange={(v) => {
          if (!v) {
            setPendingMembershipAction(null);
            setDeleteCompanyTypedConfirm('');
          }
        }}
      >
        <DialogContent className="max-w-sm">
          {pendingMembershipAction && (() => {
            const cid = pendingMembershipAction.companyId;
            const action = pendingMembershipAction.action;
            const companyName = getMembershipCompanyName(cid);
            const isDelete = action === 'delete';
            const titleKey = isDelete ? 'memberships.delete.title' : 'memberships.leave.title';
            const descKey = isDelete ? 'memberships.delete.description' : 'memberships.leave.description';
            const confirmKey = isDelete ? 'memberships.delete.confirm' : 'memberships.leave.confirm';
            // For destructive delete we require typing the company name to confirm.
            const confirmEnabled = isDelete
              ? deleteCompanyTypedConfirm.trim() === companyName.trim() && companyName.length > 0
              : true;
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{t(titleKey)}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  {t(descKey, { company: companyName })}
                </p>
                {isDelete && (
                  <div className="space-y-2">
                    <Label className="text-xs">
                      {t('memberships.delete.typeToConfirm', { name: companyName })}
                    </Label>
                    <Input
                      value={deleteCompanyTypedConfirm}
                      onChange={(e) => setDeleteCompanyTypedConfirm(e.target.value)}
                      placeholder={companyName}
                      autoFocus
                    />
                  </div>
                )}
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPendingMembershipAction(null);
                      setDeleteCompanyTypedConfirm('');
                    }}
                    disabled={membershipActionBusy}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleConfirmMembershipAction}
                    disabled={membershipActionBusy || !confirmEnabled}
                  >
                    {membershipActionBusy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    {t(confirmKey)}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Disable Account Confirmation */}
      <Dialog open={disableAccountOpen} onOpenChange={setDisableAccountOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('profile.disableAccount')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('profile.disableAccountConfirm')}</p>
          <div className="space-y-2">
            <Label>{t('profile.typeEmail')}</Label>
            <Input
              value={disableConfirm}
              onChange={e => setDisableConfirm(e.target.value)}
              placeholder={user?.email || ''}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDisableAccountOpen(false); setDisableConfirm(''); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={disableConfirm !== user?.email}
              onClick={async () => {
                setDisableAccountOpen(false);
                setDisableConfirm('');
                try {
                  await api.auth.disableAccount();
                  toast.success(t('profile.accountDisabled'));
                } catch {
                  toast.error(t('common.error'));
                } finally {
                  logout();
                }
              }}
            >
              {t('profile.disableAccount')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation (permanent) */}
      <Dialog open={deleteAccountOpen} onOpenChange={setDeleteAccountOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-destructive">{t('profile.permanentDeleteAccount')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('profile.permanentDeleteAccountConfirm')}</p>
          <div className="space-y-2">
            <Label>{t('profile.typeEmail')}</Label>
            <Input
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder={user?.email || ''}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteAccountOpen(false); setDeleteConfirm(''); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteConfirm !== user?.email}
              onClick={async () => {
                setDeleteAccountOpen(false);
                setDeleteConfirm('');
                try {
                  await api.auth.deleteAccount();
                  toast.success(t('profile.accountPermanentlyDeleted'));
                } catch {
                  toast.error(t('common.error'));
                } finally {
                  logout();
                }
              }}
            >
              {t('profile.permanentDeleteAccount')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
